import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { EncoderAlertPayload, TriageSession, EncoderSampleData } from '@shared/types';

// Extended session type with latest sample
export interface TriageSessionWithSample extends TriageSession {
  latestSample?: EncoderSampleData;
}

// localStorage key for muted alerts
const MUTED_ALERTS_KEY = 'admin-portal:muted-alerts';

// Helper to get alert key for muting
export function getAlertKey(encoderId: string, alertType: string): string {
  return `${encoderId}:${alertType}`;
}

// Load muted alerts from localStorage
function loadMutedAlerts(): Set<string> {
  try {
    const stored = localStorage.getItem(MUTED_ALERTS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed);
      }
    }
  } catch (e) {
    console.warn('Failed to load muted alerts from localStorage:', e);
  }
  return new Set();
}

// Save muted alerts to localStorage
function saveMutedAlerts(muted: Set<string>): void {
  try {
    localStorage.setItem(MUTED_ALERTS_KEY, JSON.stringify([...muted]));
  } catch (e) {
    console.warn('Failed to save muted alerts to localStorage:', e);
  }
}

interface AlertState {
  // Active alerts keyed by encoderId -> alertType -> payload
  alerts: Map<string, Map<string, EncoderAlertPayload>>;
  // Active triage sessions with latest sample data
  triageSessions: TriageSessionWithSample[];
  // SSE connection status
  sseStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  // Muted alerts (stored in localStorage) - keys are "encoderId:alertType"
  mutedAlerts: Set<string>;

  // Alert actions
  addAlert: (alert: EncoderAlertPayload) => void;
  updateAlert: (encoderId: string, alertType: string, lastAnomalyAt: number, currentValue?: number) => void;
  removeAlert: (encoderId: string, alertType: string) => void;
  clearAlertsForEncoder: (encoderId: string) => void;
  setAlerts: (alerts: EncoderAlertPayload[]) => void;

  // Mute actions
  muteAlert: (encoderId: string, alertType: string) => void;
  unmuteAlert: (encoderId: string, alertType: string) => void;
  isAlertMuted: (encoderId: string, alertType: string) => boolean;
  clearMutedAlerts: () => void;

  // Triage session actions
  setTriageSessions: (sessions: TriageSessionWithSample[]) => void;
  addTriageSession: (session: TriageSessionWithSample) => void;
  removeTriageSession: (encoderId: string) => void;
  updateTriageSessionSample: (encoderId: string, sample: EncoderSampleData) => void;

  // SSE status
  setSseStatus: (status: AlertState['sseStatus']) => void;

  // Computed getters (excluding muted alerts)
  getAlertCount: () => number;
  getTriageCount: () => number;
  getAlertsByEncoder: (encoderId: string) => EncoderAlertPayload[];
  getAllAlerts: () => EncoderAlertPayload[];
  getCriticalCount: () => number;
  getWarningCount: () => number;
  getMutedCount: () => number;
}

export const useAlertStore = create<AlertState>()(
  devtools(
    (set, get) => ({
      alerts: new Map(),
      triageSessions: [],
      sseStatus: 'disconnected',
      mutedAlerts: loadMutedAlerts(),

      addAlert: (alert) =>
        set((state) => {
          const newAlerts = new Map(state.alerts);
          if (!newAlerts.has(alert.encoderId)) {
            newAlerts.set(alert.encoderId, new Map());
          }
          newAlerts.get(alert.encoderId)!.set(alert.alertType, alert);
          return { alerts: newAlerts };
        }),

      updateAlert: (encoderId, alertType, lastAnomalyAt, currentValue) =>
        set((state) => {
          const newAlerts = new Map(state.alerts);
          const encoderAlerts = newAlerts.get(encoderId);
          if (encoderAlerts) {
            const existing = encoderAlerts.get(alertType);
            if (existing) {
              encoderAlerts.set(alertType, {
                ...existing,
                lastAnomalyAt,
                ...(currentValue !== undefined && { currentValue }),
              });
            }
          }
          return { alerts: newAlerts };
        }),

      removeAlert: (encoderId, alertType) =>
        set((state) => {
          const newAlerts = new Map(state.alerts);
          const encoderAlerts = newAlerts.get(encoderId);
          if (encoderAlerts) {
            encoderAlerts.delete(alertType);
            if (encoderAlerts.size === 0) {
              newAlerts.delete(encoderId);
            }
          }
          return { alerts: newAlerts };
        }),

      clearAlertsForEncoder: (encoderId) =>
        set((state) => {
          const newAlerts = new Map(state.alerts);
          newAlerts.delete(encoderId);
          return { alerts: newAlerts };
        }),

      setAlerts: (alertList) =>
        set(() => {
          const newAlerts = new Map<string, Map<string, EncoderAlertPayload>>();
          for (const alert of alertList) {
            if (!newAlerts.has(alert.encoderId)) {
              newAlerts.set(alert.encoderId, new Map());
            }
            newAlerts.get(alert.encoderId)!.set(alert.alertType, alert);
          }
          return { alerts: newAlerts };
        }),

      muteAlert: (encoderId, alertType) =>
        set((state) => {
          const key = getAlertKey(encoderId, alertType);
          const newMuted = new Set(state.mutedAlerts);
          newMuted.add(key);
          saveMutedAlerts(newMuted);
          return { mutedAlerts: newMuted };
        }),

      unmuteAlert: (encoderId, alertType) =>
        set((state) => {
          const key = getAlertKey(encoderId, alertType);
          const newMuted = new Set(state.mutedAlerts);
          newMuted.delete(key);
          saveMutedAlerts(newMuted);
          return { mutedAlerts: newMuted };
        }),

      isAlertMuted: (encoderId, alertType) => {
        const { mutedAlerts } = get();
        return mutedAlerts.has(getAlertKey(encoderId, alertType));
      },

      clearMutedAlerts: () =>
        set(() => {
          saveMutedAlerts(new Set());
          return { mutedAlerts: new Set() };
        }),

      setTriageSessions: (sessions) => set({ triageSessions: sessions }),

      addTriageSession: (session) =>
        set((state) => {
          // Dedupe - SSE event may have already added this session
          if (state.triageSessions.some((s) => s.encoderId === session.encoderId)) {
            return state;
          }
          return { triageSessions: [...state.triageSessions, session] };
        }),

      removeTriageSession: (encoderId) =>
        set((state) => ({
          triageSessions: state.triageSessions.filter((s) => s.encoderId !== encoderId),
        })),

      updateTriageSessionSample: (encoderId, sample) =>
        set((state) => ({
          triageSessions: state.triageSessions.map((s) =>
            s.encoderId === encoderId ? { ...s, latestSample: sample } : s
          ),
        })),

      setSseStatus: (status) => set({ sseStatus: status }),

      // Getters exclude muted alerts
      getAlertCount: () => {
        const { alerts, mutedAlerts } = get();
        let count = 0;
        for (const encoderAlerts of alerts.values()) {
          for (const alert of encoderAlerts.values()) {
            const key = getAlertKey(alert.encoderId, alert.alertType);
            if (!mutedAlerts.has(key)) {
              count++;
            }
          }
        }
        return count;
      },

      getTriageCount: () => get().triageSessions.length,

      getAlertsByEncoder: (encoderId) => {
        const { alerts, mutedAlerts } = get();
        const encoderAlerts = alerts.get(encoderId);
        if (!encoderAlerts) return [];
        return Array.from(encoderAlerts.values()).filter(
          (alert) => !mutedAlerts.has(getAlertKey(alert.encoderId, alert.alertType))
        );
      },

      getAllAlerts: () => {
        const { alerts, mutedAlerts } = get();
        const all: EncoderAlertPayload[] = [];
        for (const encoderAlerts of alerts.values()) {
          for (const alert of encoderAlerts.values()) {
            const key = getAlertKey(alert.encoderId, alert.alertType);
            if (!mutedAlerts.has(key)) {
              all.push(alert);
            }
          }
        }
        // Sort by severity (critical first) then by encoderId
        return all.sort((a, b) => {
          const severityOrder = { critical: 0, warning: 1, info: 2 };
          const aSev = severityOrder[a.severity] ?? 3;
          const bSev = severityOrder[b.severity] ?? 3;
          if (aSev !== bSev) return aSev - bSev;
          return a.encoderId.localeCompare(b.encoderId);
        });
      },

      getCriticalCount: () => {
        const { alerts, mutedAlerts } = get();
        let count = 0;
        for (const encoderAlerts of alerts.values()) {
          for (const alert of encoderAlerts.values()) {
            const key = getAlertKey(alert.encoderId, alert.alertType);
            if (!mutedAlerts.has(key) && alert.severity === 'critical') {
              count++;
            }
          }
        }
        return count;
      },

      getWarningCount: () => {
        const { alerts, mutedAlerts } = get();
        let count = 0;
        for (const encoderAlerts of alerts.values()) {
          for (const alert of encoderAlerts.values()) {
            const key = getAlertKey(alert.encoderId, alert.alertType);
            if (!mutedAlerts.has(key) && alert.severity === 'warning') {
              count++;
            }
          }
        }
        return count;
      },

      getMutedCount: () => {
        const { alerts, mutedAlerts } = get();
        let count = 0;
        for (const encoderAlerts of alerts.values()) {
          for (const alert of encoderAlerts.values()) {
            const key = getAlertKey(alert.encoderId, alert.alertType);
            if (mutedAlerts.has(key)) {
              count++;
            }
          }
        }
        return count;
      },
    }),
    { name: 'alert-store' }
  )
);
